/* ============================================================
   Upakarna, Native Personalisation Builder
   Vanilla JS, no external dependencies.
   Composite live mockup: charm photos are background-removed
   client-side (canvas chroma-key on near-white + auto-crop to
   the charm's bounding box) and overlaid on a blank base image
   with a soft drop shadow so they look physically attached. If
   background removal can't run (CORS-blocked source, or a busy/
   non-white product photo the chroma key can't cleanly key out),
   each charm instead renders as a small white "chip", circular,
   drop-shadowed, object-fit:contain, so it always reads as a
   deliberate, legible token instead of a half-blended artifact.

   Two preview layouts:
   - Flat row (default): charms render at a UNIFORM, COUNT-
     INDEPENDENT height (--pz-h, percent of the preview height,
     18% unless overridden per-product via data-w), evenly spaced
     with a consistent gap; 2 charms or 6, same size, same gap,
     centered in a row, LEFT TO RIGHT IN PICK ORDER.
   - Arc mode (data-arc on the preview row, from custom.preview_arc
     "cx,cy,r,startDeg,endDeg"): charms curve along that arc
     instead, evenly spaced, matching upk-band-charm-builder's arc
     math exactly (including the negative-spread fix so 1–3 charms
     still cluster centrally instead of spanning the whole arc).
     Sized by HEIGHT (data-w overrides, default 13% of frame), not
     width, so charms of different natural width (e.g. a narrow
     "I" vs a wide "M") render at the same visual scale instead of
     the same width. Pitch between charms is 30°. A small synthetic
     jump-ring is drawn at each anchor point so every charm reads
     as "hung from the chain" the same way regardless of how that
     charm's own product photo happened to be cropped. The FIRST
     picked charm is placed at the arc's END angle and the LAST at
     its START angle, for the typical bottom-of-chain arc (start
     and end both between 0° and 180°, straddling 90°), increasing
     theta moves from screen-right to screen-left, so this mapping
     is what makes the on-chain order read left-to-right in pick
     order, matching the flat-row layout and the "Your Selection"
     chip row instead of rendering backwards.

   Adds base product + selected letter/charm addon variants to
   cart in one AJAX call, linked by a shared _pzid property.
   Gift box ("Is it a gift?") rides in the same add call when ticked.

   COLOUR-VARIANT INVENTORY (2026-08-25, metafield-driven): when
   data.beltVariants (from Liquid) has an entry resolvable for the
   selected Finish, that variant, not the untracked base variant, is
   sent as the main cart line, so add-to-cart deducts real inventory
   for the chosen colour. Liquid can source beltVariants two ways:
     - Per-product metafields (custom.personalisation_variant_gold /
       _rosegold), keys in that case are placeholders (__GOLD__ /
       __ROSE__), and data.beltFinishKeys maps each real Finish swatch
       value to the right placeholder. resolveBeltVariant() below
       checks beltFinishKeys first, so this is transparent to the rest
       of the component.
     - The legacy "Watchbelt"-label lookup, keys are the real Finish
       values directly, and beltFinishKeys is empty, so resolution
       falls through to the finish value unchanged (original behaviour).
   Products with neither source configured get an empty beltVariants
   object and fall back to the original base-variant behaviour, unchanged.
   ============================================================ */
(function () {
  'use strict';
  if (customElements.get('upk-personalise')) return;

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function keyOutWhite(img) {
    var w = img.naturalWidth, h = img.naturalHeight;
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0);
    var d = ctx.getImageData(0, 0, w, h);
    var p = d.data;
    var minX = w, minY = h, maxX = -1, maxY = -1;
    var kept = 0;
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var i4 = (y * w + x) * 4;
        var r = p[i4], g = p[i4 + 1], b = p[i4 + 2];
        var mx = Math.max(r, g, b), mn = Math.min(r, g, b);
        var sat = mx - mn;
        if (mn > 222 && sat < 30) {
          p[i4 + 3] = 0;
        } else if (mn > 195 && sat < 38) {
          p[i4 + 3] = Math.round(p[i4 + 3] * (222 - mn) / 27);
        }
        if (p[i4 + 3] > 24) {
          kept++;
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    // If keying removed almost nothing (busy/non-white photo) or almost
    // everything (over-aggressive match), the result won't read as a
    // clean cutout, bail out to the chip fallback instead of shipping
    // a mangled charm.
    var total = w * h;
    if (maxX < 0 || kept < total * 0.02 || kept > total * 0.97) return null;
    ctx.putImageData(d, 0, 0);
    var pad = Math.max(1, Math.round(Math.max(w, h) * 0.005));
    minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
    maxX = Math.min(w - 1, maxX + pad); maxY = Math.min(h - 1, maxY + pad);
    var cw = maxX - minX + 1, ch = maxY - minY + 1;
    var c2 = document.createElement('canvas');
    c2.width = cw; c2.height = ch;
    c2.getContext('2d').drawImage(c, minX, minY, cw, ch, 0, 0, cw, ch);
    return c2.toDataURL('image/png');
  }

  class UpkPersonalise extends HTMLElement {
    connectedCallback() {
      var dataEl = this.querySelector('[data-pz-data]');
      try { this.data = JSON.parse(dataEl.textContent); } catch (e) { this.data = { items: [] }; }
      this.min = parseInt(this.dataset.min || '1', 10);
      this.max = parseInt(this.dataset.max || '6', 10);
      this.basePrice = parseInt(this.dataset.basePrice || '0', 10);
      this.baseVariant = this.dataset.baseVariant;
      this.beltVariants = this.data.beltVariants || {};
      this.beltFinishKeys = this.data.beltFinishKeys || {};
      this.ctaText = this.dataset.cta || 'Add to bag';
      this.grid = this.querySelector('[data-pz-grid]');
      this.pickedEl = this.querySelector('[data-pz-picked]');
      this.previewRow = this.querySelector('[data-pz-preview]');
      this.previewBase = this.querySelector('[data-pz-base]');
      this.countEl = this.querySelector('[data-pz-count]');
      this.totalEl = this.querySelector('[data-pz-total]');
      this.errorEl = this.querySelector('[data-pz-error]');
      this.atcBtn = this.querySelector('[data-pz-atc]');
      this.atcLabelEl = this.querySelector('[data-pz-atc-label]');
      this.finishLabel = this.querySelector('[data-pz-finish-label]');
      this.finishSuffix = this.querySelector('[data-pz-finish-suffix]');
      this.typeInput = this.querySelector('[data-pz-type]');
      this.deltaEl = this.querySelector('[data-pz-delta]');
      this.helpEl = this.querySelector('[data-pz-help]');
      this.helpDefault = this.helpEl ? this.helpEl.textContent : '';
      this.textChip = this.querySelector('[data-pz-textchip]');
      this.missingText = this.dataset.missing || 'Add your initial';
      this.finish = null;
      this.beltVariant = null;
      this._cutCache = {};
      this._pvToken = 0;

      this.arc = null;
      if (this.previewRow && this.previewRow.dataset.arc) {
        var parts = this.previewRow.dataset.arc.split(',').map(function (n) { return parseFloat(n); });
        if (parts.length === 5 && parts.every(function (n) { return !isNaN(n); })) {
          this.arc = { cx: parts[0], cy: parts[1], r: parts[2], start: parts[3], end: parts[4] };
          this.previewRow.classList.add('upk-pz__mock-row--arc');
        }
      }

      var pdpRoot = this.closest('[data-upk-pdp]');
      this.giftBox = this.querySelector('[data-upk-giftbox]') || (pdpRoot ? pdpRoot.querySelector('[data-upk-giftbox]') : null);
      this.giftCheck = this.giftBox ? this.giftBox.querySelector('[data-upk-giftbox-check]') : null;
      if (this.giftCheck) {
        var gSelf = this;
        this.giftCheck.addEventListener('change', function () {
          gSelf.giftBox.classList.toggle('is-on', gSelf.giftCheck.checked);
          gSelf.renderSummary();
        });
      }
      var active = this.querySelector('[data-pz-finish].is-active');
      if (!active) {
        active = this.querySelector('[data-pz-finish]');
        if (active) { active.classList.add('is-active'); active.setAttribute('aria-checked', 'true'); }
      }
      if (active) this.finish = active.getAttribute('data-pz-finish');
      this.beltVariant = this.resolveBeltVariant(this.finish);
      this.selection = [];
      var fixed = this.previewRow ? parseInt(this.previewRow.getAttribute('data-w') || '0', 10) : 0;
      this.charmH = fixed > 0 ? fixed : (this.arc ? 13 : 18);
      if (this.previewRow && !this.arc) this.previewRow.style.setProperty('--pz-h', this.charmH + '%');
      this.bind();
      this.updatePreviewBase();
      this.renderGrid();
      this.renderSummary();
      this.hideDefaults();
    }

    hideDefaults() {
      var info = this.closest('.product__info-container');
      if (!info) return;
      var q = info.querySelector('.product-form__quantity');
      if (q) q.style.display = 'none';
    }

    giftOn() { return !!(this.giftCheck && this.giftCheck.checked); }
    giftPrice() { return this.giftOn() ? (parseInt(this.giftBox.dataset.giftPrice, 10) || 0) : 0; }

    resolveBeltVariant(finish) {
      if (!finish) return null;
      var key = (this.beltFinishKeys && this.beltFinishKeys[finish]) || finish;
      return this.beltVariants[key] || null;
    }

    bind() {
      var self = this;
      this.querySelectorAll('[data-pz-finish]').forEach(function (btn) {
        btn.addEventListener('click', function () { self.setFinish(btn); });
      });
      this.grid.addEventListener('click', function (e) {
        var tile = e.target.closest('[data-pz-id]');
        if (!tile || tile.disabled) return;
        self.addItem(tile.getAttribute('data-pz-id'));
      });
      this.pickedEl.addEventListener('click', function (e) {
        var x = e.target.closest('[data-pz-remove]');
        if (!x) return;
        self.removeAt(parseInt(x.getAttribute('data-pz-remove'), 10));
      });
      this.atcBtn.addEventListener('click', function () { self.addToCart(); });
      if (this.typeInput) this.typeInput.addEventListener('input', function () { self.applyTyped(); });
    }

    // Phase 4: the text field picks letter tiles as the customer types. Letters A to Z map to single-character
    // items for the current finish; charms stay where they were tapped; anything else is explained in one line.
    isLetterItem(i) { return !!(i && i.label && i.label.length === 1); }
    applyTyped() {
      var up = (this.typeInput.value || '').toUpperCase();
      var pool = this.itemsForFinish();
      var byLabel = {};
      pool.forEach(function (i) { if (i.label && i.label.length === 1 && i.avail !== false) byLabel[i.label.toUpperCase()] = i; });
      var charms = this.selection.filter(function (i) { return !(i.label && i.label.length === 1); });
      var letters = [], kept = '', unsupported = false, missing = '';
      for (var k = 0; k < up.length; k++) {
        var ch = up[k];
        if (/\s/.test(ch)) continue;
        if (!/[A-Z]/.test(ch)) { unsupported = true; continue; }
        if (!byLabel[ch]) { missing = ch; continue; }
        if (letters.length + charms.length >= this.max) break;
        letters.push(byLabel[ch]); kept += ch;
      }
      this.selection = letters.concat(charms);
      if (this.typeInput.value !== kept) this.typeInput.value = kept;
      if (this.helpEl) {
        if (unsupported) this.helpEl.textContent = 'Only letters A to Z for now. Numbers and symbols are charms, tap one below.';
        else if (missing) this.helpEl.textContent = 'The letter ' + missing + ' is not available in this finish.';
        else this.helpEl.textContent = this.helpDefault;
      }
      this.renderGrid();
      this.renderSummary();
    }
    syncTyped() {
      if (!this.typeInput) return;
      var self = this;
      var v = this.selection.filter(function (i) { return self.isLetterItem(i); }).map(function (i) { return i.label.toUpperCase(); }).join('');
      if (this.typeInput.value !== v) this.typeInput.value = v;
      if (this.helpEl) this.helpEl.textContent = this.helpDefault;
    }
    renderDelta() {
      if (!this.deltaEl) return;
      var prices = {};
      this.itemsForFinish().forEach(function (i) { prices[i.price || 0] = true; });
      var list = Object.keys(prices).map(Number).sort(function (a, b) { return a - b; });
      if (!list.length || (list.length === 1 && list[0] === 0)) this.deltaEl.textContent = 'Letters included';
      else if (list.length === 1) this.deltaEl.textContent = '+ ' + this.formatMoney(list[0]) + ' each';
      else this.deltaEl.textContent = 'from + ' + this.formatMoney(list.find(function (p) { return p > 0; }) || 0) + ' each';
    }

    setFinish(btn) {
      var val = btn.getAttribute('data-pz-finish');
      if (btn.disabled || val === this.finish) return;
      this.querySelectorAll('[data-pz-finish]').forEach(function (b) {
        var on = b === btn;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      this.finish = val;
      this.beltVariant = this.resolveBeltVariant(this.finish);
      this.updatePreviewBase();
      var pool = this.itemsForFinish();
      var byLabel = {};
      pool.forEach(function (i) { byLabel[i.label] = i; });
      this.selection = this.selection
        .map(function (i) { return byLabel[i.label] || null; })
        .filter(Boolean);
      this.syncTyped();
      this.renderGrid();
      this.renderSummary();
    }

    updatePreviewBase() {
      if (!this.previewBase) return;
      var rose = this.previewBase.getAttribute('data-base-rose');
      var def = this.previewBase.getAttribute('data-base-default');
      var useRose = rose && this.finish && this.finish.toLowerCase().indexOf('rose') !== -1;
      var next = useRose ? rose : def;
      if (next && this.previewBase.getAttribute('src') !== next) {
        this.previewBase.setAttribute('src', next);
      }
    }

    itemsForFinish() {
      var f = this.finish;
      return (this.data.items || []).filter(function (i) { return !f || !i.color || i.color === f; });
    }

    counts() {
      var c = {};
      this.selection.forEach(function (i) { c[i.id] = (c[i.id] || 0) + 1; });
      return c;
    }

    renderGrid() {
      var counts = this.counts();
      var html = '';
      this.itemsForFinish().forEach(function (i) {
        var n = counts[i.id] || 0;
        var caption = (i.label && i.label.length > 2)
          ? '<span class="upk-pz__tile-caption">' + escapeHtml(i.label) + '</span>'
          : '<span class="upk-pz__tile-caption"></span>';
        html += '<div class="upk-pz__cell">'
          + '<button type="button" class="upk-pz__tile' + (n ? ' is-active' : '') + '" data-pz-id="' + i.id + '" aria-label="' + escapeHtml(i.label) + '"' + (i.avail === false ? ' disabled' : '') + '>'
          + '<img src="' + i.img + '" alt="' + escapeHtml(i.label) + '" loading="lazy" width="90" height="90">'
          + (n ? '<span class="upk-pz__tile-badge">' + n + '</span>' : '')
          + '</button>' + caption + '</div>';
      });
      this.grid.innerHTML = html;
      if (this.finishLabel) this.finishLabel.textContent = this.finish || '';
      if (this.finishSuffix) this.finishSuffix.textContent = this.finish ? ' - ' + this.finish : '';
    }

    cutout(item) {
      var self = this;
      if (this._cutCache[item.id]) return this._cutCache[item.id];
      this._cutCache[item.id] = new Promise(function (resolve) {
        var im = new Image();
        im.crossOrigin = 'anonymous';
        im.onload = function () {
          try {
            resolve(keyOutWhite(im));
          } catch (e) {
            resolve(null);
          }
        };
        im.onerror = function () { resolve(null); };
        im.src = item.imgLg || item.img;
      });
      return this._cutCache[item.id];
    }

    renderPreview() {
      var self = this;
      if (!this.previewRow) return;
      // No plain base photo yet (custom.personalisation_preview_image): show the letter chips only, never draw over the product photo (Cowork checks 5 Oct, system B).
      if (this.hasAttribute('data-pz-noletters')) { this.previewRow.innerHTML = ''; return; }
      var n = this.selection.length;
      var token = ++this._pvToken;
      if (!n) { this.previewRow.innerHTML = ''; return; }
      var sel = this.selection.slice();
      Promise.all(sel.map(function (i) { return self.cutout(i); })).then(function (srcs) {
        if (token !== self._pvToken) return;
        if (self.arc) {
          self.renderPreviewArc(sel, srcs);
        } else {
          self.previewRow.innerHTML = srcs.map(function (src, idx) {
            var i = sel[idx];
            if (src) {
              return '<img class="upk-pz__mock-charm" src="' + src + '" alt="' + escapeHtml(i.label) + '">';
            }
            return '<img class="upk-pz__mock-charm upk-pz__mock-charm--chip" src="' + (i.imgLg || i.img) + '" alt="' + escapeHtml(i.label) + '">';
          }).join('');
        }
      });
    }

    renderPreviewArc(sel, srcs) {
      var arc = this.arc;
      var n = sel.length;
      var size = this.charmH;
      var fullSpread = arc.end - arc.start;
      var pitchDeg = 30;
      var maxAbs = Math.min(Math.abs(fullSpread), pitchDeg * (n - 1));
      var spread = fullSpread < 0 ? -maxAbs : maxAbs;
      var mid = (arc.start + arc.end) / 2;
      var start2 = mid - spread / 2;
      var end2 = mid + spread / 2;

      var html = '';
      sel.forEach(function (c, i) {
        // Reversed on purpose: the first-picked charm goes at the arc's END
        // angle and the last-picked at its START angle. For a typical
        // bottom-of-chain arc (start/end both between 0° and 180°), higher
        // theta sits further left on screen, so this makes pick order read
        // left-to-right instead of backwards.
        var t = n === 1 ? 0.5 : (n - 1 - i) / (n - 1);
        var thetaDeg = n === 1 ? mid : start2 + (end2 - start2) * t;
        var theta = thetaDeg * Math.PI / 180;
        var x = arc.cx + arc.r * Math.cos(theta);
        var y = arc.cy + arc.r * Math.sin(theta);
        var src = srcs[i];
        var cls = src ? 'upk-pz__mock-charm' : 'upk-pz__mock-charm upk-pz__mock-charm--chip';
        var finalSrc = src || (c.imgLg || c.img);
        html += '<span class="upk-pz__mock-ring" style="left:' + x.toFixed(2) + '%;top:' + y.toFixed(2) + '%"></span>';
        html += '<img class="' + cls + '" src="' + finalSrc + '" alt="' + escapeHtml(c.label) + '" style="left:' + x.toFixed(2) + '%;top:' + y.toFixed(2) + '%;height:' + size + '%;width:auto">';
      });
      this.previewRow.innerHTML = html;
    }

    renderSummary() {
      if (!this.selection.length) {
        this.pickedEl.innerHTML = '<span class="upk-pz__picked-empty">Type your initials, or tap a letter or charm.</span>';
      } else {
        this.pickedEl.innerHTML = this.selection.map(function (i, idx) {
          return '<span class="upk-pz__chip"><img src="' + i.img + '" alt="' + escapeHtml(i.label) + '"><button type="button" class="upk-pz__chip-x" data-pz-remove="' + idx + '" aria-label="Remove ' + escapeHtml(i.label) + '">&times;</button></span>';
        }).join('');
      }
      this.countEl.textContent = this.selection.length;
      var baseAmt = this.beltVariant ? (this.beltVariant.price || 0) : this.basePrice;
      var total = baseAmt + this.selection.reduce(function (s, i) { return s + (i.price || 0); }, 0) + this.giftPrice();
      var money = this.formatMoney(total);
      this.totalEl.textContent = money;
      var ready = this.selection.length >= this.min;
      this.atcBtn.disabled = !ready;
      if (this.atcLabelEl) this.atcLabelEl.textContent = ready ? (this.ctaText + ' \u00B7 ' + money) : this.missingText;
      if (this.textChip) {
        var self2 = this;
        var txt = this.selection.filter(function (i) { return self2.isLetterItem(i); }).map(function (i) { return i.label.toUpperCase(); }).join(' ');
        this.textChip.textContent = txt;
        this.textChip.hidden = !txt;
      }
      this.renderDelta();
      this.renderPreview();
      this.hideError();
    }

    addItem(id) {
      if (this.selection.length >= this.max) {
        this.showError('You can pick up to ' + this.max + ' letters or charms.');
        if (this.typeInput) this.typeInput.value = this.typeInput.value;
        return;
      }
      var item = (this.data.items || []).find(function (i) { return String(i.id) === String(id); });
      if (!item) return;
      this.selection.push(item);
      this.syncTyped();
      this.renderGrid();
      this.renderSummary();
    }

    removeAt(idx) {
      this.selection.splice(idx, 1);
      this.syncTyped();
      this.renderGrid();
      this.renderSummary();
    }

    showError(msg) { this.errorEl.textContent = msg; this.errorEl.hidden = false; }
    hideError() { this.errorEl.hidden = true; }

    formatMoney(cents) {
      var fmt = this.data.moneyFormat || 'Rs. {{amount}}';
      var whole = Math.round(cents / 100);
      var s = whole.toLocaleString('en-IN');
      // Thousands comma and a non-breaking space after the rupee sign (Cowork checks 5 Oct).
      return fmt.replace(/<[^>]+>/g, '').replace(/\{\{\s*amount[^}]*\}\}/, s).replace(/^(\D*\S)\s+/, '$1\u00a0');
    }

    busy(on) {
      this.atcBtn.setAttribute('aria-busy', on ? 'true' : 'false');
      var sp = this.atcBtn.querySelector('.loading__spinner');
      if (sp) sp.classList.toggle('hidden', !on);
    }

    addToCart() {
      var self = this;
      if (this.selection.length < this.min) {
        this.showError(this.min === 1 ? 'Add at least one letter or charm.' : 'Add at least ' + this.min + ' letters or charms.');
        return;
      }
      var hasBeltMap = Object.keys(this.beltVariants).length > 0;
      if (hasBeltMap) {
        if (!this.finish || !this.beltVariant) {
          this.showError('Choose a finish first.');
          return;
        }
        if (this.beltVariant.avail === false) {
          this.showError('That finish is out of stock right now.');
          return;
        }
      }
      var totalCheck = (this.beltVariant ? (this.beltVariant.price || 0) : this.basePrice) + this.selection.reduce(function (s, i) { return s + (i.price || 0); }, 0);
      if (totalCheck <= 0) {
        this.showError('This piece cannot be ordered online yet. Message us on WhatsApp and we will make it for you.');
        return;
      }
      var mainVariantId = this.beltVariant ? this.beltVariant.id : this.baseVariant;
      var pzid = 'pz-' + Date.now();
      var props = {
        'Personalisation': this.selection.map(function (i) { return i.label; }).join(' + '),
        '_pzid': pzid,
        '_pz_head': 'true',
        '_pz_edit': window.location.pathname
      };
      if (this.finish) props['Finish'] = this.finish;
      var byId = this.counts();
      var items = [{ id: Number(mainVariantId), quantity: 1, properties: props }];
      Object.keys(byId).forEach(function (id) {
        items.push({ id: Number(id), quantity: byId[id], properties: { '_pzid': pzid } });
      });
      if (this.giftOn()) {
        var gvid = parseInt(this.giftBox.dataset.giftVariantId, 10);
        if (gvid) {
          var gProps = { 'Gift Wrap': 'Yes' };
          var gMsg = this.giftBox.querySelector('[data-upk-giftbox-msg]');
          if (gMsg && gMsg.value.trim()) gProps['Gift Message'] = gMsg.value.trim();
          items.push({ id: gvid, quantity: 1, properties: gProps });
        }
      }
      var url = ((window.routes && window.routes.cart_add_url) || '/cart/add') + '.js';
      this.busy(true);
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items, sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname })
      })
        .then(function (r) { return r.json(); })
        .then(function (state) {
          if (state.status && Number(state.status) >= 400) {
            throw new Error(state.description || state.message || 'Could not add to cart.');
          }
          var drawer = document.querySelector('cart-drawer');
          if (drawer && state.sections && typeof drawer.renderContents === 'function') {
            drawer.renderContents(state);
          } else {
            window.location.href = (window.routes && window.routes.cart_url) || '/cart';
            return;
          }
          if (window.publish && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'upk-personalise' });
          }
          if (self.giftCheck && self.giftCheck.checked) {
            self.giftCheck.checked = false;
            self.giftBox.classList.remove('is-on');
          }
          self.selection = [];
          self.syncTyped();
          self.renderGrid();
          self.renderSummary();
        })
        .catch(function (err) { self.showError(err.message || 'Could not add to cart.'); })
        .finally(function () { self.busy(false); });
    }
  }

  customElements.define('upk-personalise', UpkPersonalise);
})();
